import { inject, Injectable, signal, WritableSignal } from '@angular/core';
import { map, Observable, of } from 'rxjs';
import { PlanningType } from '../types/planning.type';
import { VisibilityType } from '../types/visibility.type';
import { Coordinate } from '@kinomap-dev/ng-ui-lib/core';
import { SpeedMode } from '../types/speed-mode.type';
import { AudioMode } from '../types/audio-mode.type';
import { AbstractControl, FormGroup } from '@angular/forms';
import { DialogRef } from '@angular/cdk/dialog';
import { ModalService } from './modal-service';
import { ContentType } from '../pages/content-creation/content-creation';
import { SPORT_TYPES, SportType, WorkoutSportType } from '../types/sport.type';
import { WorkoutMode } from '@kinomap-dev/ng-ui-lib/core';
import { SectionModelType } from '../types/section.type';
import { WorkoutSourceType } from '../types/workout.type';
import { MediaBlockService } from './media-block-service';
import { COACHING_WORKOUT_MODE_BY_SPORT } from '../configs/sections-editor/coaching-workout-mode.config';
import { WorkoutDetails, WorkoutInstruction } from '../types/workout-details.type';
import { convertApiWorkoutIntervalsToSections } from '../utils/convert-api-workout-to-sections.helpers';

export type PublicationFormValue = {
  visibility: VisibilityType | null;
  planning: PlanningType | null;
  planningDate: Date | string | null;
  planningTime: string | null;
};

type ConfirmResetAction = 'exit' | 'save' | 'cancel';

@Injectable({
  providedIn: 'root',
})
export class CreateContentService {
  modalService: ModalService = inject(ModalService);
  private readonly mediaBlockService: MediaBlockService = inject(MediaBlockService);

  /**
   * Type de contenu en cours de création. Porté par le service (et non par le
   * composant ContentCreation) pour rester lisible après sa destruction,
   * notamment par `uploadGuard` sur /content-creation/upload.
   */
  public contentType: WritableSignal<ContentType | undefined> = signal(undefined);

  public editedContentId: WritableSignal<number | undefined> = signal(undefined);

  // Media Block
  public videoThumbnailUrl: WritableSignal<string | undefined> = signal(undefined);
  public videoOriginalName: WritableSignal<string | undefined> = signal(undefined);
  public coordinates: WritableSignal<Coordinate[]> = signal([]);
  public videoExtention: WritableSignal<string | undefined> = signal(undefined);
  public gpsExtention: WritableSignal<string | undefined> = signal(undefined);

  // Information Block
  public vehiculeId: WritableSignal<number | undefined> = signal(undefined);
  public languageId: WritableSignal<number | undefined> = signal(undefined);
  public title: WritableSignal<string | undefined> = signal(undefined);
  public sport: WritableSignal<SportType | undefined> = signal(undefined);
  public description: WritableSignal<string | undefined> = signal(undefined);
  public recordedWith: WritableSignal<string | undefined> = signal(undefined);
  public tagsId: WritableSignal<number[]> = signal([]);

  // Training Block (workout)
  public workoutSport: WritableSignal<WorkoutSportType | undefined> = signal(undefined);
  public workoutMode: WritableSignal<WorkoutMode | undefined> = signal(undefined);
  public workoutThemes: WritableSignal<string[]> = signal([]);
  public workoutSections: WritableSignal<SectionModelType[]> = signal([]);
  public workoutSource: WritableSignal<WorkoutSourceType> = signal('create');
  public workoutImportedFile: WritableSignal<File | undefined> = signal(undefined);
  public workoutSelectedSectionsIds: WritableSignal<number[]> = signal([]);
  public workoutInstructions: WritableSignal<WorkoutInstruction[]> = signal([]);

  // Intervalles (coaching)
  public coachingSport: WritableSignal<SportType | undefined> = signal(undefined);
  public coachingWorkoutMode: WritableSignal<WorkoutMode | undefined> = signal(undefined);
  public coachingSections: WritableSignal<SectionModelType[]> = signal([]);
  public coachingSelectedSectionsIds: WritableSignal<number[]> = signal([]);

  // Global
  public uploadDate: WritableSignal<Date | undefined> = signal(new Date());
  public planningDate: WritableSignal<Date | null> = signal(null);
  public publicationFormValue: WritableSignal<PublicationFormValue | undefined> = signal(undefined);
  public recordDate: WritableSignal<Date | undefined> = signal(undefined);

  // Recommandations Block
  public speedMode: WritableSignal<SpeedMode | undefined> = signal(undefined);
  public audioMode: WritableSignal<AudioMode | undefined> = signal(undefined);

  // Publication Block
  public visibility: WritableSignal<VisibilityType | undefined> = signal(undefined);
  public planningType: WritableSignal<PlanningType | undefined> = signal(undefined);

  public isDeleted: WritableSignal<boolean> = signal(false);

  // Form Valid
  public gpsTrackFormIsValid: WritableSignal<boolean> = signal(false);
  public scenicFormIsValid: WritableSignal<boolean> = signal(false);
  public coachingFormIsValid: WritableSignal<boolean> = signal(false);
  public workoutFormIsValid: WritableSignal<boolean> = signal(false);

  public reset(): void {
    this.contentType.set(undefined);
    this.editedContentId.set(undefined);
    this.videoThumbnailUrl.set(undefined);
    this.coordinates.set([]);
    this.vehiculeId.set(undefined);
    this.languageId.set(undefined);
    this.title.set(undefined);
    this.sport.set(undefined);
    this.description.set(undefined);
    this.recordedWith.set(undefined);
    this.tagsId.set([]);
    this.workoutSport.set(undefined);
    this.workoutMode.set(undefined);
    this.workoutThemes.set([]);
    this.workoutSections.set([]);
    this.workoutSelectedSectionsIds.set([]);
    this.workoutSource.set('create');
    this.workoutImportedFile.set(undefined);
    this.workoutInstructions.set([]);
    this.coachingSport.set(undefined);
    this.coachingWorkoutMode.set(undefined);
    this.coachingSections.set([]);
    this.coachingSelectedSectionsIds.set([]);
    this.uploadDate.set(new Date());
    this.planningDate.set(null);
    this.publicationFormValue.set(undefined);
    this.recordDate.set(undefined);
    this.speedMode.set(undefined);
    this.audioMode.set(undefined);
    this.visibility.set(undefined);
    this.planningType.set(undefined);
    this.isDeleted.set(false);
    this.videoOriginalName.set(undefined);
    this.videoExtention.set(undefined);
    this.gpsExtention.set(undefined);
    this.gpsTrackFormIsValid.set(false);
    this.scenicFormIsValid.set(false);
    this.coachingFormIsValid.set(false);
    this.workoutFormIsValid.set(false);
  }

  public loadWorkout(workout: WorkoutDetails): void {
    this.reset();
    this.contentType.set('workout');
    this.editedContentId.set(workout.id);
    this.title.set(workout.name);
    this.description.set(workout.description ?? undefined);
    this.workoutThemes.set(workout.themes ?? []);
    this.workoutSport.set(workout.sport as WorkoutSportType);
    this.workoutMode.set(workout.mode);
    this.workoutSections.set(convertApiWorkoutIntervalsToSections(workout.intervals, workout.mode));
    this.workoutInstructions.set(workout.instructions ?? []);
    this.isDeleted.set(workout.status === 'deleted');
    this.visibility.set(workout.status === 'deleted' ? 'private' : workout.status);
    this.planningType.set('immediate');
  }

  public updatePublication(values: PublicationFormValue): void {
    this.visibility.set(values.visibility ?? undefined);
    this.planningType.set(values.planning ?? undefined);
    this.publicationFormValue.set(values);
    const date: Date | null = values.planningDate ? new Date(values.planningDate) : null;
    if (date && values.planningTime) {
      const [hours, minutes]: number[] = values.planningTime.split(':').map(Number);
      date.setHours(hours, minutes, 0, 0);
    }
    this.planningDate.set(date);
  }

  public setSharedSport(sport: WorkoutSportType | undefined): void {
    if (sport && (SPORT_TYPES as readonly string[]).includes(sport)) {
      this.sport.set(sport as SportType);
    }
  }

  public restoreForm(form: FormGroup, contentType: ContentType): void {
    const workoutSport: WorkoutSportType | undefined = this.workoutSport() ?? this.sport();
    const values: Record<string, unknown> = {
      'mediaBlock.videoFile': this.mediaBlockService.videoFile(),
      'mediaBlock.coordinates': this.coordinates(),
      'mediaBlock.syncroTime': this.mediaBlockService.progressInVideo(),
      'informationBlock.title': this.title(),
      'informationBlock.description': this.description(),
      'informationBlock.recordedWith': this.recordedWith(),
      'informationBlock.vehiculeId': this.vehiculeId(),
      'informationBlock.languageId': this.languageId(),
      'informationBlock.sport': this.sport(),
      'informationBlock.theme': this.workoutThemes()[0],
      'recommandationBlock.speedMode': this.speedMode(),
      'recommandationBlock.audioMode': this.audioMode(),
      'publicationBlock.visibility': this.visibility(),
      'publicationBlock.planning': this.planningType(),
      'publicationBlock.planningDate': this.publicationFormValue()?.planningDate,
      'publicationBlock.planningTime': this.publicationFormValue()?.planningTime,
    };

    if (contentType === 'workout') {
      values['trainingBlock.sport'] = workoutSport;
      if (this.workoutSport()) values['trainingBlock.workoutMode'] = this.workoutMode();
    }

    if (contentType === 'coaching' && !this.coachingSport() && this.sport()) {
      this.coachingSport.set(this.sport());
      this.coachingWorkoutMode.set(COACHING_WORKOUT_MODE_BY_SPORT[this.sport()!]);
    }

    let restored: boolean = false;
    Object.entries(values).forEach(([path, value]: [string, unknown]) => {
      const control: AbstractControl | null = form.get(path);
      if (!control || this.isEmpty(value) || control.value === value) return;
      control.setValue(value);
      restored = true;
    });
    if (restored) form.markAsDirty();
  }

  private isEmpty(value: unknown): boolean {
    return (
      value === undefined ||
      value === null ||
      value === '' ||
      (Array.isArray(value) && !value.length)
    );
  }

  /**
   * Vérifie si l'utilisateur peut quitter le formulaire en cours.
   * Si le formulaire a été modifié, affiche une modale de confirmation avec 3 options :
   * - Sauvegarder : stocke les données dans localStorage et autorise la sortie
   * - Quitter : supprime la sauvegarde locale et réinitialise le service
   * - Annuler : reste sur le formulaire
   *
   * @param form Le formulaire à vérifier
   * @param contentType Le type de contenu (scenic, coaching, workout, gps-only) pour la clé localStorage
   * @returns Observable<boolean> - true si l'utilisateur peut quitter, false sinon
   */
  public canDeactivate(form: FormGroup<any>, contentType: ContentType): Observable<boolean> {
    if (form.pristine) {
      return of(true);
    }

    const dialogRef: DialogRef<ConfirmResetAction, unknown> =
      this.modalService.openConfirmation<ConfirmResetAction>(
        {
          title: 'confirm_exit.title',
          message: 'confirm_exit.message',
          buttons: [
            { action: 'exit', label: 'confirm_exit.leave', style: 'link' },
            { action: 'save', label: 'common.save', style: 'primary' },
            { action: 'cancel', label: 'confirm_exit.back_to_edit', style: 'primary' },
          ],
        },
        '720px',
      );
    return dialogRef.closed.pipe(
      map((action: ConfirmResetAction | boolean | undefined) => {
        if (typeof action === 'boolean') return action;
        switch (action) {
          case 'save':
            const { mediaBlock, ...formValueWithoutMedia } = form.value;
            localStorage.setItem(contentType + '_form', JSON.stringify(formValueWithoutMedia));
            return true;
          case 'exit':
            if (!this.editedContentId()) localStorage.removeItem(contentType + '_form');
            this.reset();
            this.mediaBlockService.reset();
            return true;
          case 'cancel':
          default:
            return false;
        }
      }),
    );
  }
}
